/**
 * AJAX add-to-bag for FFP signature collection cards with cart drawer integration.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class FfpSignatureCollection extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#bindMobileImageToggle();
  }

  #bindMobileImageToggle() {
    this.querySelectorAll('[data-ffp-card-media]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      if (!link.querySelector('.ffp-signature__image--hover')) return;

      link.addEventListener('click', (event) => {
        if (window.matchMedia('(min-width: 750px)').matches) return;

        if (!link.classList.contains('ffp-signature__media-link--alt')) {
          event.preventDefault();
          link.classList.add('ffp-signature__media-link--alt');
        }
      });
    });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ffp-add-to-bag]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#addToCart(button, variantId);
  };

  /** @returns {string[]} */
  #getCartSectionIds() {
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    return sectionIds;
  }

  /** @returns {Promise<any>} */
  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {HTMLButtonElement} button @param {string} variantId */
  async #addToCart(button, variantId) {
    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    const sectionIds = this.#getCartSectionIds();
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });

      const payload = await response.json();
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Could not add to bag.');
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ffp-signature-collection',
          itemCount: 1,
          sections: payload.sections,
          didError: false,
        },
      });

      button.textContent = 'Added!';
      window.setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
      }, 1500);
    } catch (error) {
      this.dispatchEvent(
        new CartErrorEvent({
          error: error.message || 'Add to cart failed',
          code: 'INVALID',
        })
      );

      try {
        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'ffp-signature-collection' },
        });
      } catch {
        deferredEventPromise.reject(error);
      }

      button.textContent = 'Try again';
      button.disabled = false;
    }
  }
}

if (!customElements.get('ffp-signature-collection')) {
  customElements.define('ffp-signature-collection', FfpSignatureCollection);
}
